import './GroupRow.scss';
import { useState } from 'react';
import { Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { KWELLO_ICON, KwelloSvgIcon } from '../../icon/KwelloSvgIcon';
import style from './GroupOptions.module.scss';
import { DATASET_INITIAL_ID } from '../../../constants';
import { useAppSelector } from '../../../hooks/redux';
import { RootState } from '../../../../common/store';
import { selectCommonFilters } from '../../../store/slices/common-filters.slice';
import { DataItem } from './GroupOptions';
import { HandleCheckBoxChange } from '../AutoCompleteTreeFilter';
import { FilterValueObject } from 'common/models/filter.model';
import ClickableDiv from 'common/components/clickableDiv/ClickableDiv';
import GroupItemLabel from './GroupItemLabel';

interface GroupRowProps {
  required: boolean;
  selectedFilters: FilterValueObject[];
  item: DataItem;
  parents: DataItem[];
  handleCheckBoxChange: (data: HandleCheckBoxChange) => void;
  selectedGroups: { [key: string]: DataItem[] };
}

export default function GroupRow({
  required,
  selectedFilters,
  item,
  parents,
  handleCheckBoxChange,
  selectedGroups,
}: Readonly<GroupRowProps>) {
  const filters = useAppSelector((state: RootState) =>
    selectCommonFilters(state, DATASET_INITIAL_ID)
  );

  const selected =
    filters[item.filterType]?.value?.findIndex(
      (val: FilterValueObject) => val.value === item.value
    ) > -1;
  const [rowExpanded, setRowExpanded] = useState(false);

  const disabled =
    required &&
    item.filterType === 'Question' &&
    !selected &&
    selectedGroups?.selectedQuestions?.length > 0;

  const onClickRow = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();

    if ((event.target as HTMLInputElement).type === 'checkbox') {
      return;
    }

    setRowExpanded(!rowExpanded);
  };

  const hasChildren = item?.items?.length > 0;

  const onCheckboxChange = (event: CheckboxChangeEvent) => {
    if (event.value && !rowExpanded) {
      setRowExpanded(true);
    }

    handleCheckBoxChange({
      parents: parents,
      children: item.items,
      item,
      value: !selected,
    });
  };

  return (
    <>
      <ClickableDiv
        className={`${style[hasChildren ? 'group-item-child' : 'group-last-item']} k-list-item ${disabled ? 'disabled' : ''}`}
        onClick={onClickRow}
        disabled={disabled}
      >
        <Checkbox checked={selected} onChange={onCheckboxChange} disabled={disabled} />
        <GroupItemLabel name={item.name ?? ''} alias={item.alias} />
        {hasChildren && (
          <div className={style.chevron}>
            <KwelloSvgIcon
              icon={rowExpanded ? KWELLO_ICON.CHEVRON_UP_ICON : KWELLO_ICON.CHEVRON_DOWN_ICON}
            />
          </div>
        )}
      </ClickableDiv>

      {!disabled &&
        item?.items &&
        rowExpanded &&
        item.items.map((childItem, index) => (
          <GroupRow
            required={required}
            selectedFilters={selectedFilters}
            parents={[...parents, item]}
            key={`${childItem.value}-${index}`}
            item={childItem}
            handleCheckBoxChange={handleCheckBoxChange}
            selectedGroups={selectedGroups}
          />
        ))}
    </>
  );
}
